import qs from 'qs';

import type { paths } from '@luxuryescapes/contract-svc-vendor';

import { json_headers, request } from './common';

type JsonContent<T> = T extends { content: { 'application/json': infer TContent } } ? TContent : never;
type OperationResponse<TOperation, TStatus extends number> = TOperation extends {
  responses: Record<TStatus, infer TResponse>;
}
  ? JsonContent<TResponse>
  : never;
type OperationRequestBody<TOperation> = TOperation extends {
  requestBody: { content: { 'application/json': infer TBody } };
}
  ? TBody
  : never;

type AdminListPendingChangesOperation = paths['/api/vendor/admin/extranet-pending-changes']['get'];
type AdminGetPendingChangeOperation = paths['/api/vendor/admin/extranet-pending-changes/{id}']['get'];
type AdminApprovePendingChangeOperation = paths['/api/vendor/admin/extranet-pending-changes/{id}/approve']['post'];
type AdminRejectPendingChangeOperation = paths['/api/vendor/admin/extranet-pending-changes/{id}/reject']['post'];
type AdminBulkApprovePendingChangesOperation = paths['/api/vendor/admin/extranet-pending-changes/bulk-approve']['post'];
type AdminBulkRejectPendingChangesOperation = paths['/api/vendor/admin/extranet-pending-changes/bulk-reject']['post'];

type ContractListExtranetPendingChangesParams = AdminListPendingChangesOperation['parameters']['query'];

export type ListExtranetPendingChangesParams = Omit<ContractListExtranetPendingChangesParams, 'page' | 'limit'> & {
  page?: number;
  limit?: number;
  propertyCategory?: string;
};

export type ListExtranetPendingChangesResponse = OperationResponse<AdminListPendingChangesOperation, 200>;
export type ExtranetPendingChange = ListExtranetPendingChangesResponse['result'][number];
export type ExtranetPendingChangeStatus = ExtranetPendingChange['status'];
export type ExtranetPendingChangeRequestType = ExtranetPendingChange['request_type'];
export type GetExtranetPendingChangeResponse = OperationResponse<AdminGetPendingChangeOperation, 200>;
export type ApproveExtranetPendingChangeResponse = OperationResponse<AdminApprovePendingChangeOperation, 200>;
export type RejectExtranetPendingChangeResponse = OperationResponse<AdminRejectPendingChangeOperation, 200>;
export type BulkApproveExtranetPendingChangesResponse = OperationResponse<AdminBulkApprovePendingChangesOperation, 200>;
export type BulkRejectExtranetPendingChangesResponse = OperationResponse<AdminBulkRejectPendingChangesOperation, 200>;
export type BulkActionResponse = BulkApproveExtranetPendingChangesResponse | BulkRejectExtranetPendingChangesResponse;
type BulkActionRequestBody = OperationRequestBody<AdminBulkApprovePendingChangesOperation>;

export default class ExtranetService {
  static basePath() {
    return window.configs.API_HOST + '/api/vendor/admin/extranet-pending-changes';
  }

  static listPendingChanges(
    params: ListExtranetPendingChangesParams = {},
    options?: { signal?: AbortSignal },
  ): Promise<ListExtranetPendingChangesResponse> {
    const query = qs.stringify(params, { skipNulls: true });
    return request(`${this.basePath()}${query ? `?${query}` : ''}`, { signal: options?.signal });
  }

  static getPendingChangeById(
    id: string,
    options?: { signal?: AbortSignal },
  ): Promise<GetExtranetPendingChangeResponse> {
    return request(`${this.basePath()}/${id}`, { signal: options?.signal });
  }

  static approveChange(id: string): Promise<ApproveExtranetPendingChangeResponse> {
    return request(`${this.basePath()}/${id}/approve`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify({}),
    });
  }

  static rejectChange(id: string): Promise<RejectExtranetPendingChangeResponse> {
    return request(`${this.basePath()}/${id}/reject`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify({}),
    });
  }

  static bulkApprove(ids: BulkActionRequestBody['ids']): Promise<BulkApproveExtranetPendingChangesResponse> {
    const body: BulkActionRequestBody = { ids };
    return request(`${this.basePath()}/bulk-approve`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify(body),
    });
  }

  static bulkReject(ids: BulkActionRequestBody['ids']): Promise<BulkRejectExtranetPendingChangesResponse> {
    const body: BulkActionRequestBody = { ids };
    return request(`${this.basePath()}/bulk-reject`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify(body),
    });
  }
}
